import React from 'react';
import JobRow from './JobRow';
import type { QueueJob, QueueSectionVariant } from './types';

interface QueueSectionProps {
  title: string;
  variant: QueueSectionVariant;
  jobs: QueueJob[];
  onViewLogs: (job: QueueJob) => void;
}

/** Column headings per variant. File and Queue are common to all sections. */
// Keep in step with VARIANTS_WITH_LOGS in types.ts: every variant listed there
// gets an Actions column, and pending - which has no logs, and so no action to
// offer - does not.
const COLUMNS: Record<QueueSectionVariant, string[]> = {
  running: ['File', 'Job ID', 'Type', 'Queue', 'Progress', 'Actions'],
  failed: ['File', 'Job ID', 'Type', 'Queue', 'Error', 'Finished', 'Actions'],
  successful: ['File', 'Job ID', 'Type', 'Queue', 'Finished', 'Actions'],
  pending: ['File', 'Job ID', 'Type', 'Queue'],
};

/**
 * One status group as a heading plus a table. Empty sections still render (with
 * a placeholder row) so the page doesn't reflow every time a poll lands.
 */
export default function QueueSection({ title, variant, jobs, onViewLogs }: QueueSectionProps) {
  const columns = COLUMNS[variant];

  return (
    <section className="server-queues__section">
      <h3>
        {title} ({jobs.length})
      </h3>
      <table className="server-queues__table">
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column}>{column}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {jobs.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="server-queues__empty">
                No jobs.
              </td>
            </tr>
          ) : (
            jobs.map((job) => (
              // unique_id is the queue's own scheduling key, but jobs can repeat
              // it across queues, so pair the two.
              <JobRow
                key={`${job.queueName}:${job.unique_id}`}
                job={job}
                variant={variant}
                onViewLogs={onViewLogs}
              />
            ))
          )}
        </tbody>
      </table>
    </section>
  );
}
